import api from './api';

export interface TaskUserRef {
  id: number;
  name: string;
  avatar: string;
  /** ISO-8601 или null, пока исполнитель не нажал «Принять задачу» */
  accepted_at?: string | null;
  submitted_at?: string | null;
  submission_comment?: string | null;
  /** null — ждёт решения проверяющего; accepted | needs_revision */
  review_outcome?: string | null;
}

export interface TaskEntityRef {
  id: number;
  name: string;
  identifier?: string;
}

export interface TaskAttachmentApi {
  id: number;
  name: string;
  size: number | null;
  mime_type: string | null;
  kind?: string;
  uploaded_by_user_id?: number | null;
}

export interface TaskPendingReviewerAction {
  performer_id: number;
  name: string;
  submitted_at: string | null;
  submission_comment: string | null;
}

export interface TaskApi {
  id: number;
  title: string;
  description: string | null;
  client_id: number | null;
  client: TaskEntityRef | null;
  project_id: number | null;
  project: TaskEntityRef | null;
  case_id: number | null;
  case: TaskEntityRef | null;
  practice_area: string | null;
  practice_areas?: string[];
  tags: string[];
  created_at: string | null;
  created_at_formatted: string | null;
  updated_at: string | null;
  updated_at_formatted: string | null;
  deadline: string | null;
  deadline_formatted: string | null;
  due_date_display: string;
  status: string;
  priority: string;
  is_overdue: boolean;
  assigner: TaskUserRef | null;
  performers: TaskUserRef[];
  observers: TaskUserRef[];
  is_favorite: boolean;
  is_pinned: boolean;
  is_private?: boolean;
  can_assigner_complete?: boolean;
  viewer_is_assigner?: boolean;
  pending_reviewer_actions?: TaskPendingReviewerAction[];
  attachments?: TaskAttachmentApi[];
}

export interface TaskActivityItem {
  id: number;
  user: { id: number | null; name: string; avatar: string };
  date: string;
  time: string;
  action: string;
  summary: string;
  comment: string | null;
  meta: Record<string, unknown> | null;
}

export interface TasksListResponse {
  data: TaskApi[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface TasksListParams {
  page?: number;
  per_page?: number;
  client_id?: number;
  project_id?: number;
  project_level_only?: boolean;
  case_level_only?: boolean;
  case_id?: number;
  /** true — только завершённые и отменённые (архив) */
  archive?: boolean;
  /** Для экспорта архива: период (YYYY-MM-DD) */
  from?: string;
  to?: string;
  /** Рабочий стол: задачи владельца дашборда (постановщик / исполнитель / приватные — наблюдатель) */
  dashboard_for_user_id?: number;
  /** Для экспорта: конкретные ids, чтобы совпало с фильтрами на фронте */
  ids?: number[];
}

export interface TaskCreatePayload {
  title: string;
  description?: string | null;
  client_id?: number | null;
  project_id?: number | null;
  case_id?: number | null;
  /** При создании не передаётся: постановщиком становится текущий пользователь (сервер). */
  assigner_id?: number;
  performer_ids?: number[];
  observer_ids?: number[];
  practice_area?: string | null;
  practice_areas?: string[];
  tags?: string[];
  deadline?: string | null;
  status?: string;
  priority?: string;
  is_private?: boolean;
}

export const tasksService = {
  async getTasks(params: TasksListParams = {}): Promise<TasksListResponse> {
    const sp = new URLSearchParams();
    if (params.page) sp.append('page', String(params.page));
    if (params.per_page) sp.append('per_page', String(params.per_page));
    if (params.client_id != null) sp.append('client_id', String(params.client_id));
    if (params.project_id != null) sp.append('project_id', String(params.project_id));
    if (params.project_level_only) sp.append('project_level_only', '1');
    if (params.case_level_only) sp.append('case_level_only', '1');
    if (params.case_id != null) sp.append('case_id', String(params.case_id));
    if (params.archive) sp.append('archive', '1');
    if (params.dashboard_for_user_id != null) {
      sp.append('dashboard_for_user_id', String(params.dashboard_for_user_id));
    }
    const q = sp.toString();
    const { data } = await api.get<TasksListResponse>(q ? `/tasks?${q}` : '/tasks');
    return data;
  },

  /**
   * Скачать Excel по списку задач (GET, blob). Параметры — как в getTasks.
   */
  async downloadTasksExcel(params: TasksListParams = {}): Promise<void> {
    const sp = new URLSearchParams();
    if (params.ids && params.ids.length > 0) sp.append('ids', params.ids.join(','));
    if (params.client_id != null) sp.append('client_id', String(params.client_id));
    if (params.project_id != null) sp.append('project_id', String(params.project_id));
    if (params.project_level_only) sp.append('project_level_only', '1');
    if (params.case_level_only) sp.append('case_level_only', '1');
    if (params.case_id != null) sp.append('case_id', String(params.case_id));
    if (params.archive) sp.append('archive', '1');
    if (params.from) sp.append('from', String(params.from));
    if (params.to) sp.append('to', String(params.to));
    if (params.dashboard_for_user_id != null) sp.append('dashboard_for_user_id', String(params.dashboard_for_user_id));

    const q = sp.toString();
    const res = await api.get<Blob>(q ? `/tasks/export/excel?${q}` : '/tasks/export/excel', {
      responseType: 'blob',
    });

    const blob = res.data;
    const ctype = String(res.headers['content-type'] || '');
    if (ctype.includes('application/json')) {
      const text = await blob.text();
      let msg = 'Не удалось сформировать Excel';
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) msg = j.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }

    const header = String(res.headers['content-disposition'] || '');
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
    let filename: string | null = null;
    if (utf8?.[1]) {
      try {
        filename = decodeURIComponent(utf8[1].trim());
      } catch {
        filename = utf8[1].trim();
      }
    }
    if (!filename) {
      const simple = /filename="([^"]+)"/i.exec(header) || /filename=([^;\s]+)/i.exec(header);
      filename = simple?.[1]?.trim() ?? null;
    }

    const name = filename || (params.archive ? 'tasks-archive.xlsx' : 'tasks.xlsx');
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },

  async getTask(id: number): Promise<TaskApi> {
    const { data } = await api.get<TaskApi>(`/tasks/${id}`);
    return data;
  },

  async getTaskActivity(id: number): Promise<TaskActivityItem[]> {
    const { data } = await api.get<TaskActivityItem[]>(`/tasks/${id}/activity`);
    return data;
  },

  async createTask(payload: TaskCreatePayload): Promise<TaskApi> {
    const { data } = await api.post<TaskApi>('/tasks', payload);
    return data;
  },

  async updateTask(id: number, payload: Partial<TaskCreatePayload>): Promise<TaskApi> {
    const { data } = await api.put<TaskApi>(`/tasks/${id}`, payload);
    return data;
  },

  async uploadTaskAttachment(taskId: number, file: File): Promise<TaskAttachmentApi> {
    const body = new FormData();
    body.append('file', file);
    const { data } = await api.post<TaskAttachmentApi>(`/tasks/${taskId}/attachments`, body);
    return data;
  },

  async deleteTaskAttachment(taskId: number, attachmentId: number): Promise<void> {
    await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
  },

  async downloadTaskAttachment(taskId: number, attachmentId: number, filename: string): Promise<void> {
    const response = await api.get(`/tasks/${taskId}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  async deleteTask(id: number): Promise<void> {
    await api.delete(`/tasks/${id}`);
  },

  async toggleFavorite(id: number): Promise<{ is_favorite: boolean; is_pinned: boolean }> {
    const { data } = await api.post<{ is_favorite: boolean; is_pinned: boolean }>(
      `/tasks/${id}/toggle-favorite`,
    );
    return data;
  },

  async togglePinned(id: number): Promise<{ is_favorite: boolean; is_pinned: boolean }> {
    const { data } = await api.post<{ is_favorite: boolean; is_pinned: boolean }>(
      `/tasks/${id}/toggle-pinned`,
    );
    return data;
  },

  async acceptAsPerformer(id: number): Promise<TaskApi> {
    const { data } = await api.post<TaskApi>(`/tasks/${id}/accept-as-performer`);
    return data;
  },

  async submitTaskWork(id: number, payload: { comment?: string; files?: File[] }): Promise<TaskApi> {
    const body = new FormData();
    if (payload.comment != null && payload.comment.trim() !== '') {
      body.append('comment', payload.comment.trim());
    }
    for (const file of payload.files ?? []) {
      body.append('files[]', file);
    }
    const { data } = await api.post<TaskApi>(`/tasks/${id}/submit-work`, body);
    return data;
  },

  async reviewPerformerWork(
    id: number,
    payload: { performer_id: number; decision: 'accept' | 'needs_revision'; comment?: string },
  ): Promise<TaskApi> {
    const { data } = await api.post<TaskApi>(`/tasks/${id}/review-performer`, payload);
    return data;
  },

  async completeTask(id: number): Promise<TaskApi> {
    const { data } = await api.post<TaskApi>(`/tasks/${id}/complete`);
    return data;
  },
};
